import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  Group,
  type Material,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SphereGeometry,
  TorusGeometry,
} from 'three';
import { EMIT } from '../render/palette';
import {
  MATERIALS,
  applyContactShading,
  emitter,
  flatVertexColor,
  makeCrtTexture,
  signMaterial,
} from '../render/materials';
import { lightPool } from './props';
import type { LockKind } from '../sim/types';

/**
 * GAMEPLAY FIXTURES.
 *
 * `props.ts` is mall dressing — things that exist to make the space read. This
 * file is everything the player actually touches: containers to rob, doors to
 * breach, drop-offs to use, and the guard's control room.
 *
 * Every one of these carries a coloured lock LED. The colour is the game's most
 * important piece of readable information — from across an atrium you can tell
 * whether the thing in that shop needs the tool you chose to bring.
 */

export const LOCK_COLOR: Record<LockKind, Color> = {
  none: EMIT.exitGreen,
  force: EMIT.signAmber,
  pick: EMIT.signCyan,
  card: EMIT.lcd,
  cut: EMIT.signMagenta,
  safe: EMIT.potionA,
};

export const LOCK_LABEL: Record<LockKind, string> = {
  none: 'OPEN',
  force: 'SHUTTERED',
  pick: 'LOCKED',
  card: 'STAFF ONLY',
  cut: 'SECURED',
  safe: 'VAULT',
};

function box(w: number, h: number, d: number, mat: Material, contact = true) {
  const g = new BoxGeometry(w, h, d);
  if (contact) applyContactShading(g, { falloff: Math.max(0.6, h), strength: 0.35 });
  else flatVertexColor(g);
  const m = new Mesh(g, mat);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

/** The small status light that tells you what a thing needs. Named for the sim. */
export function lockLed(lock: LockKind, size = 0.03): Mesh {
  const led = new Mesh(
    flatVertexColor(new SphereGeometry(size, 6, 4)),
    emitter(`lock_${lock}`, LOCK_COLOR[lock], 3.0),
  );
  led.name = 'lockLed';
  return led;
}

// ---------------------------------------------------------------------------
// Containers — the things you rob
// ---------------------------------------------------------------------------

/**
 * Glass vitrine on a plinth. The jewellery store's whole personality. Lit from
 * inside so it is the brightest thing in a dark unit and pulls you toward the
 * most expensive, most protected loot in the mall.
 */
export function vitrine(lock: LockKind, width = 1.5, depth = 0.75, height = 1.05): Group {
  const g = new Group();
  g.name = 'container';

  const plinthH = height * 0.58;
  const plinth = box(width, plinthH, depth, MATERIALS.plasterDim());
  plinth.position.y = plinthH / 2;
  g.add(plinth);

  const caseH = height - plinthH;
  for (const [fw, x, ry, z] of [
    [width, 0, 0, depth / 2],
    [width, 0, Math.PI, -depth / 2],
    [depth, width / 2, Math.PI / 2, 0],
    [depth, -width / 2, -Math.PI / 2, 0],
  ] as const) {
    const pane = new Mesh(new PlaneGeometry(fw, caseH), MATERIALS.glass());
    pane.position.set(x, plinthH + caseH / 2, z);
    pane.rotation.y = ry;
    g.add(pane);
  }
  // Dark, thin frame rather than bright steel: at 0.72 metalness this lid was a
  // full-width specular slab and the single brightest shape in the room.
  const lid = box(width, 0.025, depth, MATERIALS.steelDark(), false);
  lid.position.y = height;
  g.add(lid);

  /**
   * The case interior: dark velvet deck, a thin light bar under the lid, and
   * goods sitting on it.
   *
   * The first three passes lit this with a full-area emissive pad on the deck
   * and dialled it 0.9 → 0.5 → 0.28 → 0.13 chasing a blowout that was never
   * about intensity. A horizontal quad the size of the whole case has no shape
   * to lose, so AgX clips it flat and it reads as a sheet of lit paper however
   * far you turn it down. The light has to come from a SMALL bright source with
   * dark around it, and there has to be something in the case for it to fall on
   * — an empty glass box full of glow is a lightbox no matter what.
   */
  const deck = box(width * 0.9, 0.012, depth * 0.86, MATERIALS.velvet(), false);
  deck.position.y = plinthH + 0.006;
  g.add(deck);

  const bar = new Mesh(
    new PlaneGeometry(width * 0.8, 0.03),
    emitter('vitrineBar', EMIT.shopWarm, 1.6),
  );
  bar.rotation.x = Math.PI / 2;
  bar.position.set(0, height - 0.045, -depth * 0.3);
  g.add(bar);

  // Goods. Small, on dark pads, unlit — the same trick the jeweller's own cases
  // use: a gold speck is only legible against something darker than it is.
  const trinket = emitter('caseGoods', EMIT.shopWarm, 0.9);
  const cols = Math.max(2, Math.round(width / 0.42));
  for (let i = 0; i < cols; i++) {
    const x = -width * 0.38 + (i / Math.max(1, cols - 1)) * width * 0.76;
    const z = i % 2 ? depth * 0.14 : -depth * 0.14;
    const pad = box(0.1, 0.008, 0.1, MATERIALS.velvet(), false);
    pad.position.set(x, plinthH + 0.017, z);
    g.add(pad);
    const item = new Mesh(new SphereGeometry(0.022, 6, 4), trinket);
    item.position.set(x, plinthH + 0.035, z);
    g.add(item);
  }

  const pool = lightPool(width * 1.4, EMIT.shopWarm, 0.14, 'vitrine');
  g.add(pool);

  const led = lockLed(lock);
  led.position.set(width / 2 - 0.08, plinthH - 0.06, depth / 2 + 0.005);
  g.add(led);
  return g;
}

/** Pharmacy-style locked cabinet against a wall. Faces +Z. */
export function stockCabinet(lock: LockKind, width = 1.1, height = 1.9, depth = 0.42): Group {
  const g = new Group();
  g.name = 'container';

  const carcass = box(width, height, depth, MATERIALS.plasterDim());
  carcass.position.y = height / 2;
  g.add(carcass);

  // Two glazed doors with a centre stile — reads as a controlled-drugs cabinet.
  for (const side of [-1, 1]) {
    const pane = new Mesh(
      new PlaneGeometry(width / 2 - 0.06, height - 0.2),
      MATERIALS.glass(),
    );
    pane.position.set((side * width) / 4, height / 2, depth / 2 + 0.005);
    g.add(pane);
  }
  const stile = box(0.05, height - 0.16, 0.05, MATERIALS.steelDark(), false);
  stile.position.set(0, height / 2, depth / 2 + 0.01);
  g.add(stile);

  const inner = new Mesh(
    new PlaneGeometry(width - 0.12, height - 0.24),
    emitter('cabinetGlow', EMIT.fluoro, 0.35),
  );
  inner.position.set(0, height / 2, depth / 2 - 0.02);
  g.add(inner);

  const led = lockLed(lock, 0.025);
  led.position.set(0.04, height * 0.52, depth / 2 + 0.03);
  g.add(led);
  return g;
}

/** Open retail shelving with visible stock. Unlocked — this is the easy money. */
export function stockShelf(width = 1.9, height = 1.75, depth = 0.5): Group {
  const g = new Group();
  g.name = 'container';

  for (const side of [-1, 1]) {
    const upright = box(0.05, height, depth, MATERIALS.steelDark(), false);
    upright.position.set((side * width) / 2, height / 2, 0);
    g.add(upright);
  }
  const tiers = 4;
  for (let i = 0; i < tiers; i++) {
    const y = 0.2 + (i * (height - 0.35)) / (tiers - 1);
    const shelf = box(width, 0.03, depth, MATERIALS.plasterDim(), false);
    shelf.position.y = y;
    g.add(shelf);

    // Boxed stock. Deliberately irregular so shelves do not read as a texture.
    let x = -width / 2 + 0.12;
    let n = 0;
    while (x < width / 2 - 0.12 && n < 9) {
      const bw = 0.14 + ((n * 37) % 11) / 55;
      if (x + bw > width / 2 - 0.1) break;
      const bh = 0.16 + ((n * 53) % 7) / 40;
      const b = box(bw, bh, depth * 0.7, n % 3 === 0 ? MATERIALS.timber() : MATERIALS.plaster(), false);
      b.position.set(x + bw / 2, y + bh / 2 + 0.015, 0);
      g.add(b);
      x += bw + 0.045;
      n++;
    }
  }
  const back = box(width, height, 0.02, MATERIALS.plasterDim(), false);
  back.position.set(0, height / 2, -depth / 2);
  g.add(back);

  const led = lockLed('none', 0.022);
  led.position.set(width / 2 - 0.06, height - 0.08, depth / 2);
  g.add(led);
  return g;
}

/** Floor safe. The twenty-two-second decision. */
export function floorSafe(): Group {
  const g = new Group();
  g.name = 'container';

  const body = box(0.82, 0.95, 0.78, MATERIALS.steelDark());
  body.position.y = 0.475;
  g.add(body);

  const door = box(0.7, 0.8, 0.06, MATERIALS.steel(), false);
  door.position.set(0, 0.5, 0.4);
  g.add(door);

  const dial = new Mesh(flatVertexColor(new TorusGeometry(0.09, 0.022, 6, 14)), MATERIALS.steel());
  dial.position.set(0, 0.52, 0.45);
  g.add(dial);

  const handle = box(0.04, 0.3, 0.04, MATERIALS.steel(), false);
  handle.position.set(0.24, 0.5, 0.45);
  g.add(handle);

  const led = lockLed('safe', 0.028);
  led.position.set(-0.24, 0.74, 0.44);
  g.add(led);
  return g;
}

/** Till counter. Robbing it is trivial; it is here so early rounds feel generous. */
export function checkoutCounter(width = 2.4): Group {
  const g = new Group();
  const h = 0.98;
  const body = box(width, h, 0.7, MATERIALS.plaster());
  body.position.y = h / 2;
  g.add(body);
  const top = box(width + 0.08, 0.06, 0.78, MATERIALS.timber(), false);
  top.position.y = h;
  g.add(top);

  const register = box(0.34, 0.24, 0.32, MATERIALS.plasterDim(), false);
  register.position.set(width * 0.25, h + 0.15, 0);
  g.add(register);
  const screen = new Mesh(new PlaneGeometry(0.28, 0.16), emitter('tillScreen', EMIT.crt, 1.1));
  screen.position.set(width * 0.25, h + 0.18, 0.165);
  g.add(screen);
  return g;
}

// ---------------------------------------------------------------------------
// Doors and barriers
// ---------------------------------------------------------------------------

/** Flush service door with a badge reader. Faces +Z. */
export function serviceDoor(lock: LockKind, width = 1.0, height = 2.1): Group {
  const g = new Group();
  g.name = 'door';

  const frame = box(width + 0.14, height + 0.08, 0.16, MATERIALS.steelDark(), false);
  frame.position.y = (height + 0.08) / 2;
  g.add(frame);

  const leaf = box(width, height, 0.06, MATERIALS.plasterDim());
  leaf.name = 'leaf';
  leaf.position.set(0, height / 2, 0.06);
  g.add(leaf);

  const bar = box(width * 0.7, 0.05, 0.07, MATERIALS.steel(), false);
  bar.position.set(0, height * 0.5, 0.12);
  g.add(bar);

  const plate = new Mesh(
    new PlaneGeometry(width * 0.8, 0.14),
    signMaterial({
      text: LOCK_LABEL[lock],
      widthMetres: width * 0.8,
      heightMetres: 0.14,
      color: LOCK_COLOR[lock],
      font: 'wide',
    }),
  );
  plate.position.set(0, height * 0.78, 0.095);
  g.add(plate);

  if (lock === 'card') {
    const reader = box(0.1, 0.16, 0.04, MATERIALS.rubber(), false);
    reader.position.set(width / 2 + 0.14, 1.1, 0.06);
    g.add(reader);
    const led = lockLed('card', 0.02);
    led.position.set(width / 2 + 0.14, 1.16, 0.09);
    g.add(led);
  } else {
    const led = lockLed(lock, 0.02);
    led.position.set(width / 2 - 0.1, 1.05, 0.095);
    g.add(led);
  }
  return g;
}

/** Loading dock roller door. Big, loud, and the way the good loot leaves. */
export function rollerDoor(width = 4.0, height = 3.4, openFraction = 0): Group {
  const g = new Group();
  const closedH = height * (1 - openFraction);

  const box_ = box(width + 0.3, 0.42, 0.5, MATERIALS.steelDark(), false);
  box_.position.y = height + 0.2;
  g.add(box_);

  for (const side of [-1, 1]) {
    const guide = box(0.14, height, 0.22, MATERIALS.steelDark(), false);
    guide.position.set((side * (width + 0.14)) / 2, height / 2, 0);
    g.add(guide);
  }

  const slats = Math.max(1, Math.round(closedH / 0.18));
  for (let i = 0; i < slats; i++) {
    const s = box(width, 0.16, 0.06, MATERIALS.steel(), false);
    s.position.set(0, height - i * 0.18 - 0.09, 0);
    g.add(s);
  }
  return g;
}

/**
 * The mall's front doors, and the first thing the player ever sees.
 *
 * The south wall is punched with a 9 × 4.6 opening and until now that opening
 * was literally a hole — the approach shot framed a lit atrium through a bare
 * rectangle, which reads as an unfinished level rather than a building. Panel 2
 * is entirely about the aperture: a mall entrance is a glazed screen, and the
 * mullions are what give the bright interior beyond something to be seen
 * through.
 *
 * The screen is split at door head height. Below is a bank of leaves; above is
 * a mullioned transom that carries the whole way to the soffit, because the
 * opening is twice as tall as a door and glazing the difference is what real
 * entrances do.
 *
 * One leaf stands open. It is the single most useful piece of storytelling in
 * the building: it says somebody already got in, it tells the player without a
 * prompt that this is the way, and it stops the screen reading as a sealed
 * wall the moment the round starts.
 */
export function entranceDoors(width = 9, height = 4.6, leaves = 4): Group {
  const g = new Group();
  const DOOR_H = 2.4;
  const frameMat = MATERIALS.steelDark();
  const glassMat = MATERIALS.glass();
  const leafW = width / leaves;

  /** A pane, sized and placed in the screen's own plane. */
  const pane = (w: number, h: number, x: number, y: number) => {
    const p = new Mesh(new PlaneGeometry(w, h), glassMat);
    p.position.set(x, y, 0);
    return p;
  };

  // Perimeter and the transom rail. The rail is the strongest line in the shot
  // and it is what makes the thing read as a door rather than a window.
  g.add(atY(box(width + 0.2, 0.16, 0.28, frameMat, false), height - 0.08));
  g.add(atY(box(width + 0.2, 0.14, 0.26, frameMat, false), DOOR_H));
  g.add(atY(box(width + 0.2, 0.1, 0.28, frameMat, false), 0.05));
  for (const side of [-1, 1]) {
    const jamb = box(0.18, height, 0.28, frameMat, false);
    jamb.position.set((side * (width + 0.18)) / 2, height / 2, 0);
    g.add(jamb);
  }

  for (let i = 0; i <= leaves; i++) {
    const x = -width / 2 + i * leafW;
    // Transom mullions run the full height of the glazing above the rail; the
    // ones below only exist between leaves, where two door stiles meet.
    const mull = box(0.12, height - DOOR_H, 0.24, frameMat, false);
    mull.position.set(x, DOOR_H + (height - DOOR_H) / 2, 0);
    g.add(mull);
    if (i > 0) g.add(pane(leafW - 0.12, height - DOOR_H - 0.16, x - leafW / 2, DOOR_H + (height - DOOR_H) / 2));
  }

  for (let i = 0; i < leaves; i++) {
    const hingeSide = i < leaves / 2 ? -1 : 1;
    const hingeX = -width / 2 + (i + (hingeSide < 0 ? 0 : 1)) * leafW;

    // Hinged as a group about its own jamb edge, so opening one is a rotation
    // rather than a second hand-placed mesh in a slightly wrong position.
    const leaf = new Group();
    leaf.position.set(hingeX, 0, 0);
    // The second leaf from the left, propped. Not the middle pair: an opening
    // dead-centre in a symmetrical screen looks designed, and an off-centre one
    // looks like somebody wedged it.
    if (i === 1) leaf.rotation.y = 0.85;

    const inset = -hingeSide * leafW;
    for (const [w, h, x, y] of [
      [0.09, DOOR_H, 0, DOOR_H / 2],
      [0.09, DOOR_H, inset, DOOR_H / 2],
      [Math.abs(inset), 0.09, inset / 2, DOOR_H - 0.045],
      [Math.abs(inset), 0.22, inset / 2, 0.11],
    ] as const) {
      const bar = box(w, h, 0.16, frameMat, false);
      bar.position.set(x, y, 0);
      leaf.add(bar);
    }
    leaf.add(pane(leafW - 0.2, DOOR_H - 0.45, inset / 2, DOOR_H / 2 + 0.08));

    // Push bar, on the inside face. Horizontal, hip height, and the one thing
    // on a glass door that catches a highlight from across the forecourt.
    const push = box(leafW * 0.72, 0.07, 0.07, MATERIALS.steel(), false);
    push.position.set(inset / 2, 1.06, 0.12);
    leaf.add(push);
    g.add(leaf);
  }

  /**
   * Vinyl on the glass. Deadpan and slightly too informative, which is the
   * house voice — the notice is still up, the hours are still posted, and the
   * building has been shut for hours.
   */
  const notice = new Mesh(
    new PlaneGeometry(1.5, 0.6),
    signMaterial({
      text: 'CENTRE CLOSED\nNO ENTRY AFTER 18:00\nTHANK YOU',
      widthMetres: 1.5,
      heightMetres: 0.6,
      color: EMIT.signCyan,
      font: 'wide',
      pixelsPerMetre: 200,
    }),
  );
  notice.position.set(width / 2 - leafW / 2, 1.6, 0.09);
  g.add(notice);
  return g;
}

/**
 * Backlit advertising panel. Flanks the entrance and fills shopping-centre
 * dead wall generally.
 *
 * With the doors in, the approach shot's remaining problem was forty per cent
 * of frame given over to an unbroken field of lit plaster either side of the
 * opening. That is not a lighting fault and no amount of wash fixes it — the
 * wall needs something ON it. A poster case is the correct answer twice over:
 * it is what is actually there in every real mall entrance, and the house voice
 * is corporate copy delivered completely straight.
 */
export function posterCase(width = 1.2, height = 1.8, text = '', lit = true): Group {
  const g = new Group();
  const carcass = box(width + 0.1, height + 0.1, 0.14, MATERIALS.steelDark(), false);
  g.add(carcass);

  const face = new Mesh(
    new PlaneGeometry(width, height),
    lit
      ? signMaterial({
          text,
          widthMetres: width,
          heightMetres: height,
          color: EMIT.signCyan,
          font: 'wide',
          pixelsPerMetre: 190,
        })
      : MATERIALS.plasterDim(),
  );
  face.position.z = 0.078;
  g.add(face);

  // A lit box spills onto the wall it is screwed to, and at this size the spill
  // is most of what the player registers from twenty metres out.
  if (lit) g.add(lightPool(width * 2.6, EMIT.signCyan, 0.3, 'poster'));
  return g;
}

/** Lift a mesh to a given centre height. Reads better than three assignments. */
function atY<T extends Mesh>(mesh: T, y: number): T {
  mesh.position.y = y;
  return mesh;
}

// ---------------------------------------------------------------------------
// Drop-offs
// ---------------------------------------------------------------------------

/**
 * SOLO CHUTE. A pneumatic tube in a service alcove: hopper at chest height,
 * tube climbing into the ceiling, and an indicator that runs while it cycles.
 * It should look like something the mall installed for cash, not for you.
 */
export function pneumaticChute(): Group {
  const g = new Group();
  g.name = 'dropoff-solo';

  const stand = box(0.9, 1.1, 0.7, MATERIALS.steelDark());
  stand.position.y = 0.55;
  g.add(stand);

  const hopper = new Mesh(
    applyContactShading(new CylinderGeometry(0.34, 0.24, 0.42, 8), { falloff: 0.45, strength: 0.4 }),
    MATERIALS.steel(),
  );
  hopper.position.y = 1.3;
  g.add(hopper);

  const mouth = new Mesh(
    flatVertexColor(new CylinderGeometry(0.3, 0.3, 0.02, 8)),
    emitter('chuteMouth', EMIT.signCyan, 0.7),
  );
  mouth.position.y = 1.52;
  g.add(mouth);

  const tube = new Mesh(flatVertexColor(new CylinderGeometry(0.22, 0.22, 2.2, 8)), MATERIALS.steel());
  tube.position.y = 2.6;
  g.add(tube);

  const sign = new Mesh(
    new PlaneGeometry(1.0, 0.2),
    signMaterial({
      text: 'CASH TRANSFER',
      widthMetres: 1.0,
      heightMetres: 0.2,
      color: EMIT.signCyan,
      font: 'wide',
    }),
  );
  sign.position.set(0, 1.78, 0.36);
  g.add(sign);

  const led = new Mesh(flatVertexColor(new SphereGeometry(0.035, 6, 4)), emitter('chuteLed', EMIT.exitGreen, 3));
  led.name = 'chuteLed';
  led.position.set(0.3, 1.12, 0.36);
  g.add(led);

  g.add(lightPool(2.4, EMIT.signCyan, 0.35, 'signCyan'));
  return g;
}

/**
 * LOADING DOCK PLATFORM. Two release handles, deliberately too far apart for one
 * person, which is the whole reason the dock pays better than the chute.
 */
export function dockPlatform(width = 5.0): Group {
  const g = new Group();
  g.name = 'dropoff-dock';

  const deck = box(width, 0.35, 3.0, MATERIALS.concreteDark());
  deck.position.y = 0.175;
  g.add(deck);

  const edge = box(width, 0.08, 0.12, MATERIALS.steel(), false);
  edge.position.set(0, 0.39, 1.5);
  g.add(edge);

  // Hazard chevrons along the lip. Deadpan mall safety signage, in the dark.
  const chev = new Mesh(
    new PlaneGeometry(width, 0.3),
    signMaterial({
      text: 'STAND CLEAR   STAND CLEAR',
      widthMetres: width,
      heightMetres: 0.3,
      color: EMIT.signAmber,
      font: 'wide',
    }),
  );
  chev.rotation.x = -Math.PI / 2;
  chev.position.set(0, 0.36, 1.1);
  g.add(chev);

  for (const side of [-1, 1]) {
    const post = box(0.16, 1.25, 0.16, MATERIALS.steelDark(), false);
    post.position.set((side * (width - 0.9)) / 2, 0.98, -1.1);
    g.add(post);

    const handle = box(0.1, 0.4, 0.1, MATERIALS.rubber(), false);
    handle.name = 'dockHandle';
    handle.position.set((side * (width - 0.9)) / 2, 1.7, -1.05);
    g.add(handle);

    const led = new Mesh(flatVertexColor(new SphereGeometry(0.04, 6, 4)), emitter('dockLed', EMIT.signAmber, 2.6));
    led.position.set((side * (width - 0.9)) / 2, 1.95, -1.05);
    g.add(led);
  }

  g.add(lightPool(width * 0.9, EMIT.signAmber, 0.3, 'signAmber'));
  return g;
}

// ---------------------------------------------------------------------------
// Control room (reference panel 9)
// ---------------------------------------------------------------------------

/** Bank of CRT monitors. The guard's whole interface, and the room's only light. */
export function monitorWall(cols = 4, rows = 3, cellW = 0.62, cellH = 0.48): Group {
  const g = new Group();
  const w = cols * cellW;
  const h = rows * cellH;

  const backing = box(w + 0.14, h + 0.14, 0.12, MATERIALS.steelDark(), false);
  backing.position.set(0, h / 2, -0.08);
  g.add(backing);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = -w / 2 + cellW * (c + 0.5);
      const y = cellH * (r + 0.5);

      const bezel = box(cellW - 0.04, cellH - 0.04, 0.16, MATERIALS.plasterDim(), false);
      bezel.position.set(x, y, 0);
      g.add(bezel);

      // Unique material per screen, so each feed can be swapped for a real
      // render target once the control room renders live camera views.
      const idx = r * cols + c;
      const screen = new Mesh(
        new PlaneGeometry(cellW - 0.12, cellH - 0.12),
        new MeshBasicMaterial({
          map: makeCrtTexture(idx + 1, `CAM ${String(idx + 1).padStart(2, '0')}`),
          toneMapped: true,
          fog: true,
        }),
      );
      screen.name = `feed_${idx}`;
      screen.position.set(x, y, 0.081);
      g.add(screen);
    }
  }

  // Screens light the room and nothing else does. This is the panel 9 look.
  const spill = lightPool(w * 1.1, EMIT.crt, 0.5, 'crt');
  spill.position.z = 1.2;
  g.add(spill);
  return g;
}

/** Control desk with a chair, a keyboard, and the mug of a person who left. */
export function controlDesk(width = 2.6): Group {
  const g = new Group();
  const h = 0.76;
  const top = box(width, 0.06, 0.8, MATERIALS.timber(), false);
  top.position.y = h;
  g.add(top);
  for (const side of [-1, 1]) {
    const leg = box(0.08, h, 0.7, MATERIALS.steelDark(), false);
    leg.position.set((side * (width - 0.2)) / 2, h / 2, 0);
    g.add(leg);
  }
  const keyboard = box(0.44, 0.02, 0.16, MATERIALS.rubber(), false);
  keyboard.position.set(-0.2, h + 0.04, 0.1);
  g.add(keyboard);

  const mug = new Mesh(flatVertexColor(new CylinderGeometry(0.045, 0.04, 0.1, 8)), MATERIALS.plaster());
  mug.position.set(0.5, h + 0.08, 0.05);
  g.add(mug);

  const chair = new Group();
  const seat = box(0.46, 0.07, 0.44, MATERIALS.rubber(), false);
  seat.position.y = 0.46;
  chair.add(seat);
  const back = box(0.44, 0.5, 0.06, MATERIALS.rubber(), false);
  back.position.set(0, 0.72, -0.2);
  chair.add(back);
  const post = box(0.07, 0.44, 0.07, MATERIALS.steelDark(), false);
  post.position.y = 0.23;
  chair.add(post);
  const base = new Mesh(flatVertexColor(new CylinderGeometry(0.28, 0.3, 0.05, 10)), MATERIALS.steelDark());
  base.position.y = 0.03;
  chair.add(base);
  chair.position.set(0, 0, 0.95);
  chair.rotation.y = 0.3;
  g.add(chair);
  return g;
}

/**
 * The chute console — the guard's half of the drop-off system.
 *
 * Every store's pneumatic chute terminates here, and this is where they get
 * locked. It is the one object in the building that gives the guard something to
 * DO other than look, so it is built to be read at a glance from the doorway: a
 * sloped face angled up at the seated player, one labelled rocker per chute, and
 * a lamp above each that is the only thing in the room that changes colour.
 *
 * The slope is the whole trick. A flat panel on a desk is invisible from
 * standing height and reads as a dark rectangle; tipped back thirty degrees it
 * catches the monitor spill and every switch on it throws a small shadow.
 */
export function chuteConsole(width = 1.5, channels = 6): Group {
  const g = new Group();
  const depth = 0.42;
  const carcass = box(width, 0.1, depth, MATERIALS.steelDark(), false);
  carcass.position.y = 0.05;
  g.add(carcass);

  // Tipped UP toward whoever is sitting at the desk (+z), not away from them.
  // Sign matters: the other way round the face is a dark underside and every
  // switch on it is hidden behind its own panel.
  const face = new Group();
  face.rotation.x = 0.52;
  face.position.set(0, 0.1, -0.04);
  const plate = box(width, 0.02, 0.3, MATERIALS.plaster(), false);
  plate.position.set(0, 0.09, -0.13);
  face.add(plate);

  for (let i = 0; i < channels; i++) {
    const x = -width / 2 + (width * (i + 0.5)) / channels;
    // Rocker, tipped the opposite way on a couple of them — a board where every
    // switch is in the same position looks like a texture, not a control.
    const rocker = box(width / channels - 0.06, 0.03, 0.07, MATERIALS.rubber(), false);
    rocker.position.set(x, 0.115, -0.06);
    rocker.rotation.x = i % 3 === 1 ? 0.3 : -0.3;
    face.add(rocker);

    const lamp = new Mesh(
      flatVertexColor(new BoxGeometry(0.055, 0.016, 0.055)),
      // Amber for armed, green for clear. Two channels sealed is the state the
      // room should be found in — a board that is all one colour says nothing.
      emitter(i % 4 === 2 ? 'chuteArmed' : 'chuteClear', i % 4 === 2 ? EMIT.signAmber : EMIT.exitGreen, 3.4),
    );
    lamp.position.set(x, 0.106, -0.17);
    face.add(lamp);
  }

  const legend = new Mesh(
    new PlaneGeometry(width - 0.06, 0.09),
    signMaterial({
      text: 'CHUTE ISOLATE  1-6',
      color: EMIT.lcd,
      widthMetres: width - 0.06,
      heightMetres: 0.09,
      align: 'left',
      pixelsPerMetre: 200,
    }),
  );
  legend.rotation.x = -Math.PI / 2;
  legend.position.set(0, 0.101, -0.25);
  face.add(legend);
  g.add(face);
  return g;
}

/**
 * The recorder rack — where fifteen camera feeds physically go.
 *
 * Half furniture, half exposition. The control room needs a reason to exist as a
 * ROOM rather than as a desk, and a humming cabinet of drives is it; it also
 * gives the far side of the space a tall silhouette so the guard's back is
 * against something. The LED column is the only animated-looking thing in here
 * and it is what makes the room read as running rather than abandoned.
 */
export function serverRack(height = 1.9): Group {
  const g = new Group();
  const carcass = box(0.62, height, 0.72, MATERIALS.steelDark(), false);
  carcass.position.y = height / 2;
  g.add(carcass);
  const units = Math.floor(height / 0.16) - 1;
  for (let i = 0; i < units; i++) {
    const y = 0.14 + i * 0.16;
    // Face plate, inset, so the rack reads as stacked boxes rather than a slab.
    const plate = box(0.56, 0.13, 0.02, MATERIALS.plasterDim(), false);
    plate.position.set(0, y, 0.37);
    g.add(plate);
    if (i % 3 === 1) continue; // blanking panels — not every slot is populated
    for (let j = 0; j < 3; j++) {
      const led = new Mesh(
        flatVertexColor(new BoxGeometry(0.018, 0.012, 0.006)),
        emitter(j === 1 ? 'rackWarn' : 'rackOk', j === 1 ? EMIT.signAmber : EMIT.exitGreen, 2.0),
      );
      led.position.set(-0.22 + j * 0.05, y, 0.386);
      g.add(led);
    }
  }
  return g;
}

/** Staff lockers. One door ajar, because nobody in this building tidies up. */
export function lockerBank(bays = 4): Group {
  const g = new Group();
  const w = 0.38;
  const h = 1.8;
  for (let i = 0; i < bays; i++) {
    const x = (i - (bays - 1) / 2) * w;
    const carcass = box(w - 0.01, h, 0.45, MATERIALS.steelDark(), false);
    carcass.position.set(x, h / 2, 0);
    g.add(carcass);
    const door = box(w - 0.05, h - 0.06, 0.02, MATERIALS.steel(), false);
    if (i === 1) {
      // The open one. Hinged from its left edge, swung into the room.
      door.position.set(x - w / 2 + 0.06, h / 2, 0.36);
      door.rotation.y = -0.9;
    } else {
      door.position.set(x, h / 2, 0.235);
    }
    g.add(door);
    const vent = box(w - 0.14, 0.12, 0.01, MATERIALS.steelDark(), false);
    vent.position.set(x, h - 0.22, 0.245);
    g.add(vent);
  }
  return g;
}

/**
 * Anglepoise on the desk. The only warm light in a room lit entirely by CRTs,
 * which is what stops the whole frame going one shade of green.
 */
export function deskLamp(): Group {
  const g = new Group();
  const base = new Mesh(flatVertexColor(new CylinderGeometry(0.09, 0.1, 0.02, 10)), MATERIALS.steelDark());
  base.position.y = 0.01;
  g.add(base);
  const stem = box(0.02, 0.34, 0.02, MATERIALS.steelDark(), false);
  stem.position.set(0, 0.18, 0);
  stem.rotation.z = 0.18;
  g.add(stem);
  const arm = box(0.02, 0.02, 0.26, MATERIALS.steelDark(), false);
  arm.position.set(-0.06, 0.35, 0.11);
  g.add(arm);
  const shade = new Mesh(
    flatVertexColor(new CylinderGeometry(0.05, 0.09, 0.1, 8, 1, true)),
    MATERIALS.steelDark(),
  );
  shade.position.set(-0.06, 0.32, 0.23);
  shade.rotation.x = 1.15;
  g.add(shade);
  const bulb = new Mesh(flatVertexColor(new SphereGeometry(0.035, 6, 5)), emitter('deskBulb', EMIT.shopWarm, 1.4));
  bulb.position.set(-0.06, 0.29, 0.25);
  g.add(bulb);
  const pool = lightPool(0.9, EMIT.shopWarm, 0.5, 'deskLamp');
  pool.position.set(-0.06, 0.005, 0.34);
  g.add(pool);
  return g;
}

/**
 * Key cabinet, hanging open. Twelve hooks, four of them empty.
 *
 * Pure story furniture, and cheap: the empty hooks say four people are out on
 * the floor with keys, which is a more efficient way of telling the player who
 * else is in the building than anything in the HUD could be.
 */
export function keyCabinet(): Group {
  const g = new Group();
  const carcass = box(0.5, 0.62, 0.1, MATERIALS.steelDark(), false);
  g.add(carcass);
  const back = box(0.44, 0.56, 0.01, MATERIALS.plasterDim(), false);
  back.position.z = 0.052;
  g.add(back);
  const door = box(0.48, 0.6, 0.02, MATERIALS.steelDark(), false);
  // Hinged open toward the room, so it catches light on its inner face.
  door.position.set(-0.42, 0, 0.19);
  door.rotation.y = -1.1;
  g.add(door);
  for (let i = 0; i < 12; i++) {
    const x = -0.16 + (i % 4) * 0.107;
    const y = 0.19 - Math.floor(i / 4) * 0.17;
    const hook = box(0.012, 0.03, 0.03, MATERIALS.steel(), false);
    hook.position.set(x, y, 0.07);
    g.add(hook);
    if (i % 3 === 0) continue; // the ones that are out on the floor
    const key = box(0.02, 0.08, 0.006, MATERIALS.steel(), false);
    key.position.set(x, y - 0.06, 0.08);
    g.add(key);
  }
  return g;
}

/**
 * Pinboard with a shift rota on it. Faces into the room; hang it on a wall.
 *
 * The papers are deliberately off-square. A grid of perfectly aligned rectangles
 * reads as a texture map; three sheets at four degrees apiece read as something
 * a person put there.
 */
export function pinBoard(width = 1.0, height = 0.7): Group {
  const g = new Group();
  const cork = box(width, height, 0.03, MATERIALS.timber(), false);
  g.add(cork);
  const sheets: Array<[number, number, number, number, string]> = [
    [-0.26, 0.1, 0.26, 0.34, 'NIGHT ROTA'],
    [0.06, 0.14, 0.22, 0.28, 'INCIDENT LOG'],
    [0.26, -0.14, 0.24, 0.3, 'FIRE PLAN'],
  ];
  for (const [i, [px, py, sw, sh, text]] of sheets.entries()) {
    const paper = box(sw, sh, 0.004, MATERIALS.plaster(), false);
    paper.position.set(px, py, 0.019);
    paper.rotation.z = (i - 1) * 0.07;
    g.add(paper);
    const label = new Mesh(
      new PlaneGeometry(sw * 0.86, 0.05),
      signMaterial({
        text,
        color: EMIT.lcd,
        widthMetres: sw * 0.86,
        heightMetres: 0.05,
        align: 'center',
        pixelsPerMetre: 220,
      }),
    );
    label.position.set(px, py + sh / 2 - 0.05, 0.023);
    label.rotation.z = (i - 1) * 0.07;
    g.add(label);
  }
  return g;
}

/** Wall-mounted walkie charging cradle. Diegetic explanation for the radio. */
export function radioCharger(): Group {
  const g = new Group();
  const plate = box(0.5, 0.24, 0.08, MATERIALS.plasterDim(), false);
  plate.position.y = 0.12;
  g.add(plate);
  for (let i = 0; i < 3; i++) {
    const radio = box(0.08, 0.2, 0.05, MATERIALS.rubber(), false);
    radio.position.set(-0.15 + i * 0.15, 0.22, 0.05);
    g.add(radio);
    const led = new Mesh(flatVertexColor(new SphereGeometry(0.012, 5, 4)), emitter('radioLed', EMIT.lcd, 2.5));
    led.position.set(-0.15 + i * 0.15, 0.3, 0.08);
    g.add(led);
  }
  return g;
}

// ---------------------------------------------------------------------------
// Service spaces (reference panel 4)
// ---------------------------------------------------------------------------

/** Suspended ceiling grid with a few missing tiles. Sells "behind the scenes". */
export function ceilingGrid(width: number, depth: number, y: number): Group {
  const g = new Group();
  const tile = 1.2;
  const nx = Math.max(1, Math.round(width / tile));
  const nz = Math.max(1, Math.round(depth / tile));

  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < nz; j++) {
      // A deterministic sparse gap pattern — no RNG, so screenshots stay stable.
      if ((i * 7 + j * 13) % 23 === 0) continue;
      const t = new Mesh(
        flatVertexColor(new PlaneGeometry(tile - 0.04, tile - 0.04)),
        MATERIALS.plaster(),
      );
      t.rotation.x = Math.PI / 2;
      t.position.set(-width / 2 + tile * (i + 0.5), y, -depth / 2 + tile * (j + 0.5));
      g.add(t);
    }
  }
  // Tee bars.
  for (let i = 0; i <= nx; i++) {
    const bar = box(0.04, 0.05, depth, MATERIALS.steel(), false);
    bar.position.set(-width / 2 + tile * i, y + 0.03, 0);
    g.add(bar);
  }
  for (let j = 0; j <= nz; j++) {
    const bar = box(width, 0.05, 0.04, MATERIALS.steel(), false);
    bar.position.set(0, y + 0.03, -depth / 2 + tile * j);
    g.add(bar);
  }
  return g;
}

/** Wall-mounted service ducting and conduit runs. */
export function ductRun(length: number, y: number): Group {
  const g = new Group();
  // Rectangular section with flanges every couple of metres, not a smooth tube.
  // Down a ninety-metre corridor a cylinder has no features to catch the light
  // pools, so it silhouettes as one long black wedge against the ceiling; the
  // flanges are what make it read as ductwork receding into the distance.
  const duct = box(length, 0.42, 0.34, MATERIALS.steel(), false);
  duct.position.y = y;
  g.add(duct);
  const flanges = Math.max(2, Math.round(length / 2.4));
  for (let i = 0; i <= flanges; i++) {
    const f = box(0.06, 0.5, 0.42, MATERIALS.steelDark(), false);
    f.position.set(-length / 2 + (length / flanges) * i, y, 0);
    g.add(f);
  }
  for (let i = 0; i < 3; i++) {
    const conduit = new Mesh(
      flatVertexColor(new CylinderGeometry(0.035, 0.035, length, 6)),
      MATERIALS.rubber(),
    );
    conduit.rotation.z = Math.PI / 2;
    conduit.position.set(0, y - 0.34, -0.1 + i * 0.1);
    g.add(conduit);
  }
  return g;
}

/** Vending machine. Its glow is often the only light in a service corridor. */
export function vendingMachine(): Group {
  const g = new Group();
  const body = box(0.95, 1.9, 0.75, MATERIALS.plasterDim());
  body.position.y = 0.95;
  g.add(body);

  const window = new Mesh(new PlaneGeometry(0.7, 1.3), emitter('vendGlow', EMIT.fluoro, 0.6));
  window.position.set(-0.1, 1.15, 0.38);
  g.add(window);

  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      const can = new Mesh(
        flatVertexColor(new CylinderGeometry(0.035, 0.035, 0.11, 6)),
        r % 2 === 0 ? MATERIALS.timber() : MATERIALS.rubber(),
      );
      can.position.set(-0.38 + c * 0.16, 0.62 + r * 0.31, 0.34);
      g.add(can);
    }
  }
  const panel = new Mesh(
    new PlaneGeometry(0.16, 1.2),
    signMaterial({ text: 'OUT OF ORDER', widthMetres: 0.16, heightMetres: 1.2, color: EMIT.signAmber, font: 'condensed' }),
  );
  panel.position.set(0.32, 1.2, 0.381);
  g.add(panel);

  g.add(lightPool(2.2, EMIT.fluoro, 0.28, 'cooler'));
  return g;
}

// ---------------------------------------------------------------------------
// Loading dock (reference panel 7)
// ---------------------------------------------------------------------------

/**
 * Waste skip. Big, dented, and parked where it is in the way.
 *
 * The dock measured 40% black with a lit platform floating in the middle of an
 * empty concrete box, and the fix is not more light — it is something with a
 * silhouette tall enough to occlude part of the roller door and cast a shadow
 * across the floor. A skip is the largest object that belongs in a loading bay
 * and the only one that reads at fifteen metres.
 */
export function dumpster(width = 2.4, height = 1.5, depth = 1.3): Group {
  const g = new Group();
  // Tapered: the sides slope out toward the lip, which is what stops it reading
  // as a plain crate. Lathe would be overkill; two boxes and a lid do it.
  const body = box(width, height * 0.78, depth, MATERIALS.steel());
  body.position.y = height * 0.39 + 0.16;
  g.add(body);
  const lip = box(width + 0.14, 0.1, depth + 0.12, MATERIALS.steelDark(), false);
  lip.position.y = height * 0.78 + 0.2;
  g.add(lip);

  // Lids, one flipped back. Same reasoning as the propped door: a sealed box is
  // furniture, an open one is a place somebody works.
  for (const side of [-1, 1]) {
    const lid = new Group();
    lid.position.set(0, height * 0.78 + 0.26, (side * depth) / 2);
    if (side < 0) lid.rotation.x = -2.1;
    const panel = box(width - 0.1, 0.06, depth / 2, MATERIALS.rubber(), false);
    panel.position.z = (-side * depth) / 4;
    lid.add(panel);
    g.add(lid);
  }

  for (const side of [-1, 1]) {
    for (const z of [-1, 1]) {
      const wheel = new Mesh(
        flatVertexColor(new CylinderGeometry(0.15, 0.15, 0.08, 8)),
        MATERIALS.rubber(),
      );
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set((side * (width - 0.3)) / 2, 0.15, (z * (depth - 0.3)) / 2);
      g.add(wheel);
    }
  }

  const stencil = new Mesh(
    new PlaneGeometry(width * 0.8, 0.34),
    signMaterial({
      text: 'TRADE WASTE ONLY',
      widthMetres: width * 0.8,
      heightMetres: 0.34,
      color: EMIT.signAmber,
      font: 'condensed',
    }),
  );
  stencil.position.set(0, height * 0.5, depth / 2 + 0.01);
  g.add(stencil);
  return g;
}

/** Sack truck, leaned against whatever is nearest. Pure back-of-house grammar. */
export function handTruck(): Group {
  const g = new Group();
  const frame = new Group();
  // Leaned back fifteen degrees. Standing upright it looks placed; leaning, it
  // looks abandoned mid-shift, which is the only state anything here is in.
  frame.rotation.x = -0.26;
  for (const side of [-1, 1]) {
    const rail = box(0.05, 1.25, 0.05, MATERIALS.steel(), false);
    rail.position.set(side * 0.19, 0.62, 0);
    frame.add(rail);
  }
  for (const y of [0.45, 0.85, 1.22]) {
    const rung = box(0.43, 0.04, 0.04, MATERIALS.steel(), false);
    rung.position.y = y;
    frame.add(rung);
  }
  const toe = box(0.46, 0.03, 0.24, MATERIALS.steel(), false);
  toe.position.set(0, 0.03, 0.12);
  frame.add(toe);
  for (const side of [-1, 1]) {
    const wheel = new Mesh(
      flatVertexColor(new CylinderGeometry(0.13, 0.13, 0.05, 10)),
      MATERIALS.rubber(),
    );
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(side * 0.24, 0.13, 0);
    frame.add(wheel);
  }
  g.add(frame);
  return g;
}

/**
 * Wall-mounted consumer unit with conduit fanning out of it.
 *
 * A blank concrete wall six metres high is the single largest dead surface in
 * the building. This costs nine boxes and gives it a scale reference, an
 * asymmetry, and one small green light in an otherwise sodium-orange room.
 */
export function electricalPanel(width = 0.7, height = 0.9): Group {
  const g = new Group();
  const carcass = box(width, height, 0.16, MATERIALS.steelDark());
  g.add(carcass);
  const door = box(width - 0.06, height - 0.06, 0.02, MATERIALS.plasterDim(), false);
  door.position.z = 0.09;
  g.add(door);

  const led = new Mesh(
    flatVertexColor(new BoxGeometry(0.04, 0.02, 0.02)),
    emitter('panelLive', EMIT.exitGreen, 3.0),
  );
  led.position.set(width / 2 - 0.11, -height / 2 + 0.1, 0.1);
  g.add(led);

  const label = new Mesh(
    new PlaneGeometry(width - 0.14, 0.11),
    signMaterial({
      text: 'DB-3  ISOLATE BEFORE WORK',
      widthMetres: width - 0.14,
      heightMetres: 0.11,
      color: EMIT.signAmber,
      pixelsPerMetre: 220,
    }),
  );
  label.position.set(0, height / 2 - 0.12, 0.101);
  g.add(label);

  // Conduit out of the top, splayed. Three parallel pipes read as a diagram;
  // three at slightly different angles read as an install nobody supervised.
  for (const [i, x] of [-0.18, 0, 0.2].entries()) {
    const pipe = new Mesh(
      flatVertexColor(new CylinderGeometry(0.025, 0.025, 1.6, 6)),
      MATERIALS.steel(),
    );
    pipe.position.set(x, height / 2 + 0.8, 0.02);
    pipe.rotation.z = (i - 1) * 0.06;
    g.add(pipe);
  }
  return g;
}

/** Rubber dock bumper. Two per bay, at trailer bed height. */
export function dockBumper(): Group {
  const g = new Group();
  for (let i = 0; i < 4; i++) {
    const pad = box(0.36, 0.11, 0.18, MATERIALS.rubber(), false);
    pad.position.y = 0.06 + i * 0.12;
    g.add(pad);
  }
  return g;
}

// ---------------------------------------------------------------------------
// Food court (reference panel 8)
// ---------------------------------------------------------------------------

/**
 * Illuminated menu board over a counter.
 *
 * The food court's problem is not darkness — it is that one unit was lit and
 * the other twenty metres of wall had nothing on it at all. These are cheap,
 * they are the correct height to break up a four-metre fascia, and a dead one
 * next to a live one does more for the mood than either alone.
 */
export function menuBoard(width = 2.2, height = 0.8, text = 'MENU', lit = true): Group {
  const g = new Group();
  const carcass = box(width, height, 0.12, MATERIALS.steelDark(), false);
  g.add(carcass);

  const face = new Mesh(
    new PlaneGeometry(width - 0.08, height - 0.08),
    lit
      ? signMaterial({
          text,
          widthMetres: width - 0.08,
          heightMetres: height - 0.08,
          color: EMIT.shopWarm,
          font: 'condensed',
          pixelsPerMetre: 180,
          boxed: true,
        })
      : MATERIALS.plasterDim(),
  );
  face.position.z = 0.065;
  g.add(face);

  // A lit board is a light source and has to spill onto the fascia around it,
  // or it reads as a sticker. See `lightPool` — this is the vertical case.
  if (lit) g.add(lightPool(width * 1.1, EMIT.shopWarm, 0.22, 'menu'));
  return g;
}

/**
 * Chairs stacked for the night. The single clearest signal that the room is
 * closed rather than merely empty — and the reason the loose chairs still out
 * on the floor read as somebody having been interrupted.
 */
export function chairStack(count = 5): Group {
  const g = new Group();
  for (let i = 0; i < count; i++) {
    const chair = new Group();
    const seat = box(0.4, 0.05, 0.4, MATERIALS.plasterDim());
    seat.position.y = 0.45 + i * 0.13;
    chair.add(seat);
    const back = box(0.4, 0.45, 0.05, MATERIALS.plasterDim());
    back.position.set(0, 0.68 + i * 0.13, -0.18);
    chair.add(back);
    for (const [lx, lz] of [[-0.16, -0.16], [0.16, -0.16], [-0.16, 0.16], [0.16, 0.16]]) {
      const leg = box(0.04, 0.45, 0.04, MATERIALS.steelDark(), false);
      leg.position.set(lx, 0.225 + i * 0.13, lz);
      chair.add(leg);
    }
    // Each chair a couple of degrees off the one below. A perfectly aligned
    // stack is a single extruded object; a drifting one is five chairs.
    chair.rotation.y = (i % 2 === 0 ? 1 : -1) * i * 0.03;
    chair.position.x = i * 0.012;
    g.add(chair);
  }
  return g;
}

/** Mop bucket and wringer, parked where the last shift left it. */
export function mopBucket(): Group {
  const g = new Group();
  const pail = new Mesh(
    applyContactShading(new CylinderGeometry(0.22, 0.18, 0.34, 10), { falloff: 0.4, strength: 0.4 }),
    MATERIALS.rubber(),
  );
  pail.position.y = 0.24;
  g.add(pail);
  const water = new Mesh(
    flatVertexColor(new CylinderGeometry(0.2, 0.2, 0.01, 10)),
    MATERIALS.glass(),
  );
  water.position.y = 0.36;
  g.add(water);
  const wringer = box(0.3, 0.26, 0.22, MATERIALS.plasterDim(), false);
  wringer.position.set(0, 0.54, 0.06);
  g.add(wringer);
  const handle = new Mesh(
    flatVertexColor(new CylinderGeometry(0.02, 0.02, 1.4, 6)),
    MATERIALS.timber(),
  );
  handle.position.set(0.1, 0.78, -0.16);
  handle.rotation.x = 0.3;
  handle.rotation.z = -0.18;
  g.add(handle);
  for (const side of [-1, 1]) {
    const wheel = new Mesh(
      flatVertexColor(new CylinderGeometry(0.05, 0.05, 0.03, 6)),
      MATERIALS.steelDark(),
    );
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(side * 0.16, 0.05, 0);
    g.add(wheel);
  }
  return g;
}

/** Straight stair flight with a landing-free run. Rise and run in metres. */
export function stairFlight(rise: number, run: number, width = 1.6): Group {
  const g = new Group();
  const steps = Math.max(3, Math.round(rise / 0.18));
  const tread = run / steps;
  const riserH = rise / steps;

  for (let i = 0; i < steps; i++) {
    const s = box(width, riserH, tread, MATERIALS.concrete(), false);
    s.position.set(0, riserH * (i + 0.5), tread * (i + 0.5));
    s.receiveShadow = true;
    g.add(s);
  }
  for (const side of [-1, 1]) {
    const stringer = box(0.1, 0.4, Math.hypot(rise, run), MATERIALS.steelDark(), false);
    stringer.position.set((side * (width + 0.1)) / 2, rise / 2 - 0.1, run / 2);
    stringer.rotation.x = -Math.atan2(rise, run);
    g.add(stringer);
  }
  return g;
}

/**
 * The staging bench on the forecourt: where the round actually starts.
 *
 * Every heist in this game is decided before you walk through the doors, by
 * which two of the five tools you pick up off this table. That choice deserves a
 * physical place rather than a menu — you look at a grinder and a drill lying
 * next to each other and you weigh eight seconds against twenty-two, in the
 * building's own language rather than in a list box.
 *
 * The table is empty geometry. The session lays the tools on it at round start
 * and takes them away as they are picked up, so the surface tells you what is
 * left without any UI at all.
 */
export function toolBench(width = 2.6): Group {
  const g = new Group();
  const h = 0.86;

  const top = box(width, 0.05, 0.78, MATERIALS.timber(), false);
  top.position.y = h;
  g.add(top);
  // Folding trestles, not legs — this was carried here tonight and it should
  // look it.
  for (const side of [-1, 1]) {
    for (const lean of [-1, 1]) {
      const leg = box(0.05, h, 0.05, MATERIALS.steelDark(), false);
      leg.position.set((side * (width - 0.5)) / 2, h / 2, lean * 0.3);
      leg.rotation.x = lean * 0.13;
      g.add(leg);
    }
    const brace = box(0.04, 0.04, 0.62, MATERIALS.steelDark(), false);
    brace.position.set((side * (width - 0.5)) / 2, h * 0.42, 0);
    g.add(brace);
  }

  const label = new Mesh(
    new PlaneGeometry(width * 0.8, 0.16),
    signMaterial({
      text: 'TAKE TWO',
      widthMetres: width * 0.8,
      heightMetres: 0.16,
      color: EMIT.signAmber,
      font: 'wide',
    }),
  );
  label.position.set(0, h + 0.003, 0.3);
  label.rotation.x = -Math.PI / 2;
  g.add(label);

  // A work light clamped to the end of it, so the table is the brightest thing
  // on a forecourt otherwise lit by two sodium lamps forty metres away.
  const stem = box(0.03, 0.44, 0.03, MATERIALS.steelDark(), false);
  stem.position.set(width / 2 - 0.15, h + 0.22, -0.28);
  g.add(stem);
  const shade = new Mesh(new CylinderGeometry(0.11, 0.06, 0.13, 10, 1, true), MATERIALS.steel());
  shade.position.set(width / 2 - 0.15, h + 0.44, -0.28);
  flatVertexColor(shade.geometry);
  g.add(shade);
  const bulb = new Mesh(new SphereGeometry(0.045, 8, 6), emitter('benchLamp', EMIT.fluoro, 2.1));
  bulb.position.set(width / 2 - 0.15, h + 0.4, -0.28);
  g.add(bulb);
  g.add(lightPool(2.3, EMIT.fluoro, 0.5, 'bench'));
  return g;
}
